import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import { toast } from "sonner";
import { AlertTriangle, Camera, ClipboardPaste, ScanLine, X } from "lucide-react";

// Full-screen QR scanner sheet triggered by the orange center Scan FAB.
//
// What it recognises (in priority order):
//   1. Any URL pointing at this web app or the mobile deep-link scheme
//      → `new URL(...)` is parsed and we jump to the same pathname so
//        stamp / campsite / check-in / event links all "just work".
//   2. A raw check-in token (looks like a nanoid / UUID-ish string)
//      → navigate to `/checkin/preview/<token>` (owner validation flow).
//   3. Otherwise → show the raw value in a toast so power users can
//      still see what they scanned.
//
// Also exposes a "paste a link" fallback for devices that refuse camera
// permission or don't have a rear camera (typical desktop PWA install).
export default function QRScannerModal({ open, onClose }) {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState("");

  const handleValue = useCallback((raw) => {
    const value = (raw || "").trim();
    if (!value) return;

    // 1) Try to parse as a URL first — covers https://.../checkin/... etc.
    try {
      const url = new URL(value);
      const path = url.pathname;
      if (path && path !== "/") {
        onClose();
        navigate(path + (url.search || ""));
        toast.success("Scanned — opening…");
        return;
      }
    } catch { /* not a URL, fall through */ }

    // 2) Raw token / short id — treat as a check-in preview token.
    if (/^[a-zA-Z0-9_-]{6,64}$/.test(value)) {
      onClose();
      navigate(`/checkin/preview/${value}`);
      toast.success("Scanned — opening check-in…");
      return;
    }

    // 3) Fallback — just show what we got.
    toast.message("Scanned", { description: value });
    onClose();
  }, [navigate, onClose]);

  const onScan = useCallback((results) => {
    const first = Array.isArray(results) ? results[0] : results;
    const val = first?.rawValue || first?.text || (typeof first === "string" ? first : null);
    if (val) handleValue(val);
  }, [handleValue]);

  const onScanError = useCallback((err) => {
    // Real permission errors bubble as strings/Error; benign frame
    // decode misses come through repeatedly so we ignore those.
    const msg = err?.message || String(err || "");
    if (/permiss|NotAllowed|NotFound|SecurityError|Insecure/i.test(msg)) {
      setError(msg || "Could not open the camera. Please grant camera permission.");
    }
  }, []);

  const submitManual = (e) => {
    e.preventDefault();
    handleValue(manualValue);
  };

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[110] flex flex-col"
      style={{ background: "rgba(10, 12, 10, 0.92)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Scan QR code"
      data-testid="qr-scanner-modal"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0"
           style={{ paddingTop: "max(16px, env(safe-area-inset-top))" }}>
        <button
          onClick={onClose}
          aria-label="Close scanner"
          data-testid="qr-scanner-close"
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.12)", color: "white" }}
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-white text-sm font-extrabold uppercase tracking-widest inline-flex items-center gap-2">
          <ScanLine className="w-4 h-4" style={{ color: "var(--premium)" }} />
          Scan QR
        </div>
        <div className="w-10 h-10" aria-hidden />
      </div>

      {/* Scanner viewport */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {!error ? (
          <>
            <Scanner
              onScan={onScan}
              onError={onScanError}
              constraints={{ facingMode: "environment" }}
              formats={["qr_code"]}
              styles={{
                container: { width: "100%", height: "100%" },
                video: { width: "100%", height: "100%", objectFit: "cover" },
              }}
              // Hide the library's built-in finder — we draw our own so
              // it matches the mobile app's rounded corner viewfinder.
              components={{ finder: false, tracker: undefined }}
            />
            {/* Rounded viewfinder overlay */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div
                className="w-[72vw] max-w-xs aspect-square rounded-3xl relative"
                style={{ boxShadow: "0 0 0 9999px rgba(10,12,10,0.55)" }}
              >
                <Corner cls="top-0 left-0 border-t-4 border-l-4 rounded-tl-3xl" />
                <Corner cls="top-0 right-0 border-t-4 border-r-4 rounded-tr-3xl" />
                <Corner cls="bottom-0 left-0 border-b-4 border-l-4 rounded-bl-3xl" />
                <Corner cls="bottom-0 right-0 border-b-4 border-r-4 rounded-br-3xl" />
              </div>
            </div>
          </>
        ) : (
          <div className="text-center px-8" data-testid="qr-scanner-error">
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
                 style={{ background: "rgba(255,255,255,0.12)", color: "var(--premium)" }}>
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="text-white text-lg font-extrabold">Camera unavailable</div>
            <div className="text-white/70 text-sm mt-1">
              {error} You can paste a check-in link or token below instead.
            </div>
          </div>
        )}
      </div>

      {/* Footer helpers */}
      <div
        className="shrink-0 px-6 pt-3 pb-6 flex flex-col items-center gap-3"
        style={{
          background: "rgba(0,0,0,0.6)",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
        }}
      >
        {!manualOpen ? (
          <>
            <div className="inline-flex items-center gap-2 text-white/80 text-xs">
              <Camera className="w-4 h-4" />
              Point at a check-in QR code
            </div>
            <button
              type="button"
              onClick={() => setManualOpen(true)}
              className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest px-4 py-2 rounded-full"
              style={{ background: "rgba(255,255,255,0.14)", color: "white" }}
              data-testid="qr-scanner-manual-open"
            >
              <ClipboardPaste className="w-3.5 h-3.5" /> Paste a link instead
            </button>
          </>
        ) : (
          <form onSubmit={submitManual} className="w-full max-w-sm flex flex-col gap-2"
                data-testid="qr-scanner-manual-form">
            <label className="text-[10px] font-extrabold uppercase tracking-widest text-white/80">
              Paste a check-in link or token
            </label>
            <input
              type="text"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              placeholder="https://…/checkin/preview/xxxx  or  xxxx"
              autoFocus
              className="w-full h-11 px-3 rounded-xl text-sm text-white bg-white/10 outline-none focus:bg-white/15"
              style={{ border: "1px solid rgba(255,255,255,0.15)" }}
              data-testid="qr-scanner-manual-input"
            />
            <button
              type="submit"
              className="h-11 rounded-xl text-sm font-extrabold"
              style={{ background: "var(--premium)", color: "white" }}
              data-testid="qr-scanner-manual-submit"
            >
              Open
            </button>
          </form>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

function Corner({ cls }) {
  return (
    <span
      className={`absolute w-8 h-8 ${cls}`}
      style={{ borderColor: "var(--premium)" }}
    />
  );
}
